// Copyright (c) Ping. All rights reserved.

// Brand types live in `util/ids` so the barrel doesn't fan-out
// duplicate `export *` re-exports. Imported (not re-exported) here.
import type { ConversationId, UserId } from '../../util/ids';

/** Branded poll identifier. */
export type PollId = string & { readonly __brand: 'PollId' };

/** Branded poll-option identifier. */
export type PollOptionId = string & { readonly __brand: 'PollOptionId' };

/**
 * Lifecycle state of a poll.
 *
 * - `open`     — voting is active.
 * - `closed`   — voting has ended; a single winner exists.
 * - `tied`     — voting has ended with two or more options at the top vote
 *                count.
 *
 * The cross-platform contract treats `tied` as a separate state (not a
 * sub-case of `closed`) so the host doesn't have to scan options to render
 * the trophy treatment.
 */
export type PollState = 'open' | 'closed' | 'tied';

/** Whether each voter may pick one option (`single`) or several (`multiple`). */
export type PollKind = 'single' | 'multiple';

/** Allowed close-after presets the composer offers. */
export type PollDurationPreset = '1h' | '24h' | '7d' | 'never';

export interface PollOption {
  readonly id: PollOptionId;
  readonly text: string;
  readonly voteCount: number;
  readonly votedByMe: boolean;
}

export interface Poll {
  readonly id: PollId;
  readonly conversationId: ConversationId;
  readonly question: string;
  readonly kind: PollKind;
  readonly state: PollState;
  readonly options: readonly PollOption[];
  readonly createdAt: number;
  readonly closesAt: number | null;
  readonly anonymous: boolean;
  readonly totalVotes: number;
}

/** A single voter's view in the per-option results sheet. */
export interface PollVoter {
  readonly userId: UserId;
  readonly displayName: string;
  readonly optionId: PollOptionId;
  readonly votedAt: number;
}

/** Composer reducer state. */
export interface PollComposerState {
  readonly question: string;
  readonly kind: PollKind;
  readonly anonymous: boolean;
  readonly duration: PollDurationPreset;
  readonly options: readonly PollComposerOption[];
}

export interface PollComposerOption {
  readonly id: string;
  readonly text: string;
}

export type PollComposerAction =
  | { readonly type: 'setQuestion'; readonly question: string }
  | { readonly type: 'setKind'; readonly kind: PollKind }
  | { readonly type: 'setAnonymous'; readonly anonymous: boolean }
  | { readonly type: 'setDuration'; readonly duration: PollDurationPreset }
  | { readonly type: 'addOption' }
  | { readonly type: 'removeOption'; readonly id: string }
  | { readonly type: 'setOptionText'; readonly id: string; readonly text: string }
  | { readonly type: 'reset' };

export interface PollComposerValidation {
  readonly canSubmit: boolean;
  readonly errors: ReadonlyArray<PollComposerError>;
}

export type PollComposerError =
  | 'questionEmpty'
  | 'tooFewOptions'
  | 'tooManyOptions'
  | 'duplicateOption'
  | 'optionEmpty';

/** Hard caps lifted from the Figma composer + cross-platform contract. */
export const POLL_MIN_OPTIONS = 2;
export const POLL_MAX_OPTIONS = 10;
export const POLL_QUESTION_MAX_LEN = 280;
export const POLL_OPTION_MAX_LEN = 80;
